import * as ImageManipulator from 'expo-image-manipulator';
import * as FileSystem from 'expo-file-system/legacy';
import * as VideoThumbnails from 'expo-video-thumbnails';

// Limites (bytes) — alinhados com policies do bucket
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;  // 10 MB
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;  // 50 MB
export const MAX_VIDEO_SECONDS = 60;

// Compressão: probe-then-transform.
// Resize só quando a largura excede MAX_WIDTH — evita upscale (mais peso, pior qualidade).
// Recompressão JPEG q=0.7 padroniza o output independentemente do source format.
const MAX_WIDTH = 1920;

export const compressImage = async (uri) => {
    try {
        // Probe: medir dimensões sem transformar (operação cheap)
        const probe = await ImageManipulator.manipulateAsync(uri, [], {});
        const ops = probe.width > MAX_WIDTH ? [{ resize: { width: MAX_WIDTH } }] : [];

        const result = await ImageManipulator.manipulateAsync(uri, ops, {
            compress: 0.7,
            format: ImageManipulator.SaveFormat.JPEG,
        });
        return { uri: result.uri, mimeType: 'image/jpeg' };
    } catch {
        // Fallback: usa original; caller preserva mimeType original.
        return { uri, mimeType: null };
    }
};

export const getFileSize = async (uri) => {
    try {
        const info = await FileSystem.getInfoAsync(uri, { size: true });
        return info?.size ?? null;
    } catch {
        return null;
    }
};

export const validateImageAsset = async (asset) => {
    const size = await getFileSize(asset.uri);
    if (size && size > MAX_IMAGE_BYTES) {
        throw new Error(`Imagem muito grande (${formatBytes(size)}). Máximo: ${formatBytes(MAX_IMAGE_BYTES)}.`);
    }
};

export const validateVideoAsset = async (asset) => {
    const seconds = toSeconds(asset.duration);
    if (seconds > MAX_VIDEO_SECONDS + 2) {
        throw new Error(`Vídeo muito longo (${seconds}s). Máximo: ${MAX_VIDEO_SECONDS}s.`);
    }
    const size = await getFileSize(asset.uri);
    if (size && size > MAX_VIDEO_BYTES) {
        throw new Error(`Vídeo muito pesado (${formatBytes(size)}). Máximo: ${formatBytes(MAX_VIDEO_BYTES)}.`);
    }
};

// expo-image-picker retorna ms no iOS e segundos no Android — normaliza para segundos
export const toSeconds = (duration) =>
    duration > 1000 ? Math.round(duration / 1000) : Math.round(duration || 0);

export const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// Gera miniatura (frame) de vídeo local. Frame ~1s para evitar tela preta
// de fade-in no início. Retorna null em falha — caller decide fallback.
export const generateVideoThumbnail = async (uri) => {
    try {
        const { uri: thumbUri } = await VideoThumbnails.getThumbnailAsync(uri, {
            time: 1000,
            quality: 0.7,
        });
        return thumbUri;
    } catch {
        return null;
    }
};

export const extensionFromMime = (mimeType) => {
    if (!mimeType) return 'bin';
    const sub = mimeType.split('/')[1]?.split(';')[0]?.toLowerCase();
    if (!sub) return 'bin';
    return sub === 'jpeg' ? 'jpg' : sub === 'quicktime' ? 'mov' : sub;
};
